import { ReactNode } from 'react';
import { ButtonBase, Stack, Typography } from '@mui/material';

import { ChevronRightIcon } from '../../icons';
interface IMplListItemProps {
	/**
	 * The text content to display
	 */
	text: string;
	/**
	 * Click handler for the list item
	 */
	onClick: () => void;
	/**
	 * Optional left icon element
	 */
	leftIcon?: ReactNode;
	/**
	 * Optional right icon element. Defaults to a chevron; pass a custom node to
	 * show e.g. a loading/success/error state icon.
	 */
	rightIcon?: ReactNode;
	/**
	 * Disables the button (e.g. while an action is in flight).
	 */
	disabled?: boolean;
	/**
	 * Optional data-testid attribute
	 */
	dataTestid?: string;
}

export const MplListItem = ({
	text,
	onClick,
	leftIcon,
	rightIcon = <ChevronRightIcon />,
	disabled = false,
	dataTestid,
}: IMplListItemProps) => {
	return (
		<ButtonBase
			data-testid={dataTestid}
			onClick={onClick}
			disabled={disabled}
			sx={{
				width: '100%',
				height: '64px',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				padding: '20px 8px 20px 20px',
				backgroundColor: (theme) => theme.palette.neutral.main,
				border: (theme) => `0.25px solid ${theme.palette.primary[200]}`,
				borderRadius: '8px',
				gap: '12px',
				boxShadow: '0px 8px 8px -4px #18274B14, 0px 4px 6px -4px #18274B1F',
				'&:hover': {
					backgroundColor: 'rgba(0, 0, 0, 0.04)',
				},
				'&:active': {
					backgroundColor: 'rgba(0, 0, 0, 0.08)',
				},
			}}
		>
			{leftIcon && (
				<Stack
					alignItems="center"
					justifyContent="center"
					sx={{
						width: '24px',
						height: '24px',
						flexShrink: 0,
						color: (theme) => theme.palette.primary.main,
					}}
				>
					{leftIcon}
				</Stack>
			)}

			<Typography
				variant="bu1-m"
				sx={{
					flex: 1,
					textAlign: 'left',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					whiteSpace: 'nowrap',
					color: (theme) => theme.palette.primary.main,
					marginRight: 0.5,
				}}
			>
				{text}
			</Typography>

			<Stack
				alignItems="center"
				justifyContent="center"
				sx={{
					width: '24px',
					height: '24px',
					flexShrink: 0,
					color: (theme) => theme.palette.primary.main,
				}}
			>
				{rightIcon}
			</Stack>
		</ButtonBase>
	);
};
